/**
 * Read API for the Load Data Pipeline audit dashboard.
 *
 * AUDIT ONLY. What the pipeline wrote and how the instance is coping. Market
 * data — what a lane pays, where freight is hot — comes from the public Market
 * Intel API in `./marketIntel`, not from here.
 *
 * Everything here is a GET. The pipeline's data is written by the DAT observer
 * and the Market Intel consumer; this surface exists to see what they did, not
 * to change it.
 *
 * The response shapes come from `@numeo/types`, not from a copy declared here.
 * main-service produces them and this renders them, so a second declaration
 * would compile happily while drifting from the server — in a dashboard whose
 * entire job is to be trusted about what the pipeline is doing.
 *
 * A 503 is an expected state, not an error to hide: the Market Intel database
 * is private to the VPC and reachable only by main-service, so "unavailable"
 * means the dashboard cannot see the pipeline — NOT that the pipeline stopped.
 * Callers should render that distinction rather than collapsing it.
 */

import type {
    GetLoadRequest,
    LiveSearchAuditResponse,
    LiveSearchAuditSystem,
    LoadPipelineConsumerRefusalsResponse,
    LoadPipelineConsumerResponse,
    LoadPipelineSearchCompareResponse,
    LoadPipelineSearchLookupResponse,
    LoadPipelineSearchProbeResponse,
    SearchCriteria,
    SearchLoadsRequest,
    LoadPipelineFreshnessResponse,
    LoadPipelineLanesResponse,
    LoadPipelineOverviewResponse,
    LoadPipelineQuarantineDetail,
    LoadPipelineSylectusActivityResponse,
    LoadPipelineWriterActivityResponse,
    LoadPipelineServingResponse,
    LoadPipelineWriterResponse,
} from '@numeo/types-core'
import axios from 'axios'

/**
 * Local aliases for the response shapes. These are the shared types, not
 * redefinitions — renaming a field in `@numeo/types` breaks compilation here,
 * which is the point.
 *
 * Only the two a page names explicitly are exported. The rest reach the pages
 * as the inferred return type of the call, so exporting them would be exports
 * for nobody — which is exactly what knip flags.
 */
type OverviewResponse = LoadPipelineOverviewResponse
type WriterResponse = LoadPipelineWriterResponse
type ConsumerResponse = LoadPipelineConsumerResponse
type LanesResponse = LoadPipelineLanesResponse
export type QuarantineDetail = LoadPipelineQuarantineDetail

interface Envelope<T> {
    success: boolean
    data: T
    message?: string
}

const BASE = '/gluon/load-pipeline'

export const loadPipelineApi = {
    getOverview: async (hours: number): Promise<OverviewResponse> => {
        const res = await axios.get<Envelope<OverviewResponse>>(`${BASE}/overview`, { params: { hours } })
        return res.data.data
    },

    getWriter: async (hours: number, source?: string, board?: string): Promise<WriterResponse> => {
        const res = await axios.get<Envelope<WriterResponse>>(`${BASE}/writer`, { params: { hours, source, board } })
        return res.data.data
    },

    /** One writer's hourly counters (DAT, Parade, USPS, the extension boards), from that writer's own collection. `board` narrows the extension boards to one. */
    getWriterActivity: async (source: string, hours: number, board?: string): Promise<LoadPipelineWriterActivityResponse> => {
        const res = await axios.get<Envelope<LoadPipelineWriterActivityResponse>>(`${BASE}/writer/activity`, { params: { source, hours, board } })
        return res.data.data
    },

    getSylectusActivity: async (hours: number): Promise<LoadPipelineSylectusActivityResponse> => {
        const res = await axios.get<Envelope<LoadPipelineSylectusActivityResponse>>(`${BASE}/writer/sylectus/activity`, { params: { hours } })
        return res.data.data
    },

    getFreshness: async (): Promise<LoadPipelineFreshnessResponse> => {
        const res = await axios.get<Envelope<LoadPipelineFreshnessResponse>>(`${BASE}/freshness`)
        return res.data.data
    },

    getConsumer: async (hours: number): Promise<ConsumerResponse> => {
        const res = await axios.get<Envelope<ConsumerResponse>>(`${BASE}/consumer`, { params: { hours } })
        return res.data.data
    },

    /** What every consumer of the load topic refused because an update did not parse (ADR-029). */
    getConsumerRefusals: async (hours: number): Promise<LoadPipelineConsumerRefusalsResponse> => {
        const res = await axios.get<Envelope<LoadPipelineConsumerRefusalsResponse>>(`${BASE}/consumer/refusals`, { params: { hours } })
        return res.data.data
    },

    getLanes: async (hours: number, limit: number, search?: string): Promise<LanesResponse> => {
        const res = await axios.get<Envelope<LanesResponse>>(`${BASE}/lanes`, { params: { hours, limit, search: search || undefined } })
        return res.data.data
    },
    /** Not cached server-side: a stale timing answers the wrong question. */
    getServing: async (): Promise<LoadPipelineServingResponse> => {
        const res = await axios.get<Envelope<LoadPipelineServingResponse>>(`${BASE}/serving`)
        return res.data.data
    },
    /**
     * One refusal with the payload that caused it. Not cached — it is opened
     * deliberately, one row at a time, and a stale payload would be a lie about
     * what the writer actually received.
     */
    getQuarantineDetail: async (id: string, source?: string): Promise<QuarantineDetail> => {
        const res = await axios.get<Envelope<QuarantineDetail>>(`${BASE}/quarantine/${encodeURIComponent(id)}`, { params: source ? { source } : undefined })
        return res.data.data
    },

    // ── C-Search (ADR-040). POST because a search is a body; every one is a read, and none is cached — a probe is the
    // question "what does search answer right now".

    searchProbe: async (request: SearchLoadsRequest): Promise<LoadPipelineSearchProbeResponse> => {
        const res = await axios.post<Envelope<LoadPipelineSearchProbeResponse>>(`${BASE}/search/probe`, request)
        return res.data.data
    },

    searchLookup: async (request: GetLoadRequest): Promise<LoadPipelineSearchLookupResponse> => {
        const res = await axios.post<Envelope<LoadPipelineSearchLookupResponse>>(`${BASE}/search/lookup`, request)
        return res.data.data
    },

    searchCompare: async (criteria: SearchCriteria, projectName: string): Promise<LoadPipelineSearchCompareResponse> => {
        const res = await axios.post<Envelope<LoadPipelineSearchCompareResponse>>(`${BASE}/search/compare`, { criteria, projectName })
        return res.data.data
    },

    /** One keyset page of live subscriptions from one system. Not cached: the question is "who is subscribed now". */
    getLiveSearches: async (
        system: LiveSearchAuditSystem,
        params: { projectName?: string; surface?: string; status?: string; limit?: number; cursor?: string }
    ): Promise<LiveSearchAuditResponse> => {
        const res = await axios.get<Envelope<LiveSearchAuditResponse>>(`${BASE}/search/live/${system}`, { params })
        return res.data.data
    },
}
